'use client'

import { captureException } from '@sentry/nextjs'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

import { addProductToCart, removeProductFromCart } from 'clients/segment'
import { ls } from 'utils/'
import { checkErrorInstance } from 'utils/check-error-instance'
import getEntitiesBySlugs from 'utils/get-all-entities-slugs'
import type { CompareCredit } from '../../../types'
import { logger } from 'utils/logger'

const COMPARISON_CART_VERSION = '2.2' as const

const COMPARISON_CART_LOCAL_STORAGE_KEY = 'comparisonCart' as const

const checkAndCaptureError = (originalError: unknown): void => {
  const error = checkErrorInstance(originalError)

  captureException(error)
}

export type LocalStorageComparisonCart = {
  cardIds: string[]
  version: string
}

export type ComparisonCartCards = CompareCredit.FormattedCard[]

type UpdateComparisonCartCards = (
  action: 'add' | 'remove' | 'reset' | 'replace',
  cards?: ComparisonCartCards,
) => void

type ComparisonCartCardsContextValue = {
  comparisonCartCards: ComparisonCartCards
  updateComparisonCartCards: UpdateComparisonCartCards
}

const ComparisonCartCardsContext =
  createContext<ComparisonCartCardsContextValue>({
    comparisonCartCards: [],
    updateComparisonCartCards: () => null,
  })

/**
 * @todo: The logic in this provider could use some improvements:
 * - Use callback when setting state from previous state (e.x.
 *   `setCards((currentCards) => [...currentCards, newCard])`)
 * - Simplify logic in updateComparisonCartCards (e.x. consolidate to 1 switch)
 * - Get rid of 2nd useEffect which just updates localStorage when
 *   comparisonCartCards state changes. It triggers a 2nd React render every
 *   time the comparisonCartCards state changes, which is unnecessary - could
 *   just update both state and localStorage simultaneously in
 *   updateComparisonCartCards instead
 * - Factor out and return separate functions for different kinds up updates
 *   (e.x. addCardsToComparisonCart, removeCardsFromComparisonCart, etc)
 * - Add a Zod schema for the shape of the data in local storage, use it to
 *   parse the data we pull from local storage, and its type to ensure data we
 *   put into localStorage is the right shape
 * - Another enhancement would be to add a getIsCardInComparisonCart helper to
 *   the context value, which would take in a card slug as a parameter (and
 *   should be memoized with useCallback)
 */
export const ComparisonCartCardsProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [comparisonCartCards, setComparisonCartCards] =
    useState<ComparisonCartCards>([])

  const updateComparisonCartCards: UpdateComparisonCartCards = useCallback(
    (action, cards) => {
      try {
        const getCardSlugsFromCards = (
          cardsToGetSlugsFrom?: ComparisonCartCards,
        ): string[] => (cardsToGetSlugsFrom || []).map((card) => card.slug)

        const comparisonCartCardSlugs =
          getCardSlugsFromCards(comparisonCartCards)

        let filteredCards: ComparisonCartCards

        switch (action) {
          case 'add':
            filteredCards = (cards || []).filter(
              (card) => !comparisonCartCardSlugs.includes(card.slug),
            )
            break
          case 'remove':
            filteredCards = (cards || []).filter((card) =>
              comparisonCartCardSlugs.includes(card.slug),
            )
            break
          case 'reset':
            filteredCards = comparisonCartCards
            break
          default:
            filteredCards = []
        }

        if (action !== 'replace') {
          const segmentEventHandler =
            action === 'add' ? addProductToCart : removeProductFromCart
          filteredCards.forEach((card) =>
            segmentEventHandler({
              brand: card.issuer.slug.current,
              name: card.name,
              sku: card.slug,
            }),
          )
        }

        switch (action) {
          case 'add':
            setComparisonCartCards([...comparisonCartCards, ...filteredCards])
            break
          case 'remove': {
            const slugsToRemove = getCardSlugsFromCards(filteredCards)
            const newCart = comparisonCartCards.filter(
              (card) => !slugsToRemove.includes(card.slug),
            )
            setComparisonCartCards(newCart)
            break
          }
          case 'reset':
            setComparisonCartCards([])
            break
          case 'replace':
            setComparisonCartCards(cards ?? [])
            break
          default:
            return
        }
      } catch (error) {
        checkAndCaptureError(error)
      }
    },
    [comparisonCartCards],
  )

  useEffect(() => {
    const getComparisonCartIdsAndUpdateCardData = async () => {
      try {
        const lsComparisonCart: LocalStorageComparisonCart | null = ls.getItem(
          COMPARISON_CART_LOCAL_STORAGE_KEY,
          ['version'],
          COMPARISON_CART_VERSION,
        )

        if (lsComparisonCart && lsComparisonCart.cardIds.length > 0) {
          const entities = await getEntitiesBySlugs(lsComparisonCart.cardIds)

          if (entities && !entities.error) {
            setComparisonCartCards(entities.cards)
          } else {
            logger.error('Error getting entities for comparison cart context')
          }
        }
      } catch (error) {
        checkAndCaptureError(error)
      }
    }

    getComparisonCartIdsAndUpdateCardData()
  }, [])

  useEffect(() => {
    try {
      const newLocalStorageComparisonCart: LocalStorageComparisonCart = {
        cardIds: comparisonCartCards.map((card) => card.slug),
        version: COMPARISON_CART_VERSION,
      }
      ls.setItem(
        COMPARISON_CART_LOCAL_STORAGE_KEY,
        newLocalStorageComparisonCart,
      )
    } catch (error) {
      checkAndCaptureError(error)
    }
  }, [comparisonCartCards])

  return (
    <ComparisonCartCardsContext.Provider
      value={{ comparisonCartCards, updateComparisonCartCards }}
    >
      {children}
    </ComparisonCartCardsContext.Provider>
  )
}

export const useComparisonCartCards = (): ComparisonCartCardsContextValue =>
  useContext(ComparisonCartCardsContext)
